import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '../api/api';

export default function WorkOrderModal({ order, onClose, onSaved }) {
  const [equipment, setEquipment] = useState([]);
  const [form, setForm] = useState({
    type: order?.type || 'preventive',
    equipment_id: order?.equipment_id || '',
    title: order?.title || '',
    description: order?.description || '',
    priority: order?.priority || 'medium',
    assigned_to: order?.assigned_to || '',
    estimated_hours: order?.estimated_hours || 0,
    notes: order?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/equipment').then(r => setEquipment(r.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = { ...form, estimated_hours: parseFloat(form.estimated_hours) || 0 };
      if (order?.id) {
        await api.put(`/work-orders/${order.id}`, { description: data.description, priority: data.priority, assigned_to: data.assigned_to, notes: data.notes });
      } else {
        await api.post('/work-orders', data);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {order ? 'Editar Orden de Trabajo' : 'Nueva Orden de Trabajo'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {!order && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                    data-testid="wo-type"
                  >
                    <option value="preventive">Preventivo</option>
                    <option value="corrective">Correctivo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Prioridad</label>
                  <select
                    value={form.priority}
                    onChange={e => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                    data-testid="wo-priority"
                  >
                    <option value="critical">Crítica</option>
                    <option value="high">Alta</option>
                    <option value="medium">Media</option>
                    <option value="low">Baja</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Equipo</label>
                <select
                  value={form.equipment_id}
                  onChange={e => setForm({ ...form, equipment_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                  data-testid="wo-equipment"
                >
                  <option value="">Seleccionar equipo...</option>
                  {equipment.map(eq => (
                    <option key={eq.id} value={eq.id}>{eq.name} ({eq.code})</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              required={!order}
              disabled={!!order}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none disabled:bg-gray-50"
              placeholder="Descripción breve del trabajo..."
              data-testid="wo-title"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Descripción detallada</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
              placeholder="Detalles del trabajo a realizar..."
              data-testid="wo-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Asignado a</label>
              <input
                type="text"
                value={form.assigned_to}
                onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                placeholder="Técnico responsable"
                data-testid="wo-assigned"
              />
            </div>
            {!order && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Horas estimadas</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.estimated_hours}
                  onChange={e => setForm({ ...form, estimated_hours: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                  data-testid="wo-hours"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]"
              data-testid="save-wo-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
